import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState, useEffect } from "react";
import { AppShell } from "@/components/AppShell";
import { getKyc, upsertKyc } from "@/utils/lending.functions";
import { toast } from "sonner";
import { ShieldCheck, Clock, AlertCircle } from "lucide-react";

export const Route = createFileRoute("/_authenticated/kyc")({
  head: () => ({ meta: [
    { title: "KYC Verification — LendyTrack" },
    { name: "description", content: "Submit your KYC details to unlock investing and borrowing." },
  ] }),
  component: KycPage,
});

function KycPage() {
  const q = useQuery({ queryKey: ["kyc"], queryFn: () => getKyc() });
  const qc = useQueryClient();
  const submit = useServerFn(upsertKyc);

  const [form, setForm] = useState({
    fullName: "", pan: "", aadhaarLast4: "", dateOfBirth: "",
    addressLine: "", city: "", state: "", pincode: "",
  });

  useEffect(() => {
    if (q.data) {
      setForm({
        fullName: q.data.full_name, pan: q.data.pan, aadhaarLast4: q.data.aadhaar_last4,
        dateOfBirth: q.data.date_of_birth, addressLine: q.data.address_line,
        city: q.data.city, state: q.data.state, pincode: q.data.pincode,
      });
    }
  }, [q.data]);

  const m = useMutation({
    mutationFn: () => submit({ data: { ...form, pan: form.pan.toUpperCase() } }),
    onSuccess: () => {
      toast.success("KYC submitted for review.");
      qc.invalidateQueries({ queryKey: ["kyc"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const status = q.data?.status;
  const locked = status === "verified";

  return (
    <AppShell>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">Compliance</div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">KYC Verification</h1>
        <p className="mt-1 text-sm text-muted-foreground">Required by regulation to invest or borrow on the platform.</p>
      </div>

      {status && <StatusBanner status={status} />}

      <form onSubmit={(e) => { e.preventDefault(); m.mutate(); }} className="mt-6 rounded-xl border border-border bg-card p-6 shadow-soft grid gap-4 md:grid-cols-2 max-w-3xl">
        <Field label="Full name" required>
          <input value={form.fullName} onChange={set("fullName")} required maxLength={120} disabled={locked} className="input" />
        </Field>
        <Field label="Date of birth" required>
          <input type="date" value={form.dateOfBirth} onChange={set("dateOfBirth")} required disabled={locked} className="input" />
        </Field>
        <Field label="PAN (e.g. ABCDE1234F)" required>
          <input value={form.pan} onChange={set("pan", (v) => v.toUpperCase())} required maxLength={10} disabled={locked} className="input uppercase" />
        </Field>
        <Field label="Aadhaar — last 4 digits" required>
          <input value={form.aadhaarLast4} onChange={set("aadhaarLast4")} required maxLength={4} pattern="[0-9]{4}" disabled={locked} className="input" />
        </Field>
        <Field label="Address" required className="md:col-span-2">
          <input value={form.addressLine} onChange={set("addressLine")} required maxLength={200} disabled={locked} className="input" />
        </Field>
        <Field label="City" required>
          <input value={form.city} onChange={set("city")} required maxLength={80} disabled={locked} className="input" />
        </Field>
        <Field label="State" required>
          <input value={form.state} onChange={set("state")} required maxLength={80} disabled={locked} className="input" />
        </Field>
        <Field label="PIN code" required>
          <input value={form.pincode} onChange={set("pincode")} required maxLength={6} pattern="[0-9]{6}" disabled={locked} className="input" />
        </Field>
        <div className="md:col-span-2">
          <button disabled={m.isPending || locked} className="w-full rounded-md bg-primary py-2 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50">
            {locked ? "KYC verified" : m.isPending ? "Submitting…" : status === "pending" ? "Resubmit KYC" : "Submit KYC"}
          </button>
          <p className="mt-2 text-xs text-muted-foreground">Your data is encrypted and never shared. Full Aadhaar is not stored — only the last 4 digits.</p>
        </div>
      </form>

      <style>{`.input{width:100%;border:1px solid hsl(var(--border));background:hsl(var(--background));border-radius:0.375rem;padding:0.5rem 0.75rem;font-size:0.875rem}.input:disabled{opacity:0.6;cursor:not-allowed}`}</style>
    </AppShell>
  );

  function set(key: keyof typeof form, transform?: (v: string) => string) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = transform ? transform(e.target.value) : e.target.value;
      setForm((f) => ({ ...f, [key]: v }));
    };
  }
}

function StatusBanner({ status }: { status: string }) {
  const config = {
    verified: { icon: ShieldCheck, tone: "bg-success/15 text-success border-success/30", label: "KYC verified — you're all set." },
    pending: { icon: Clock, tone: "bg-warning/15 text-warning-foreground border-warning/30", label: "Under review. Usually completes within 24 hours." },
    rejected: { icon: AlertCircle, tone: "bg-destructive/15 text-destructive border-destructive/30", label: "Rejected. Please update your details and resubmit." },
  }[status] ?? { icon: Clock, tone: "bg-muted border-border", label: status };
  const Icon = config.icon;
  return (
    <div className={`rounded-xl border p-4 flex items-center gap-3 ${config.tone}`}>
      <Icon className="h-5 w-5" />
      <span className="text-sm font-medium">{config.label}</span>
    </div>
  );
}

function Field({ label, required, className, children }: { label: string; required?: boolean; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-1">
        {label}{required && <span className="text-destructive"> *</span>}
      </label>
      {children}
    </div>
  );
}
